import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from "antd";
import {
  CloseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  PlusOutlined,
  FunnelPlotOutlined,
  TableOutlined,
  UploadOutlined,
  TrophyOutlined,
  InboxOutlined
} from "@ant-design/icons";
import { Upload } from "antd";
import { BiFilterAlt } from "react-icons/bi";
import dayjs from "dayjs";
import Papa from "papaparse";
import { Country } from "country-state-city";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Client } from "../../core/api-client";
import SalesBulkUpload from "./components/sales-bulk-upload.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import {
  createSales,
  deleteSalesById,
  getSalesList,
  updateSalesById,
} from "../../core/store/reducers/sales.slice";
import { requestGetByModuleCommon } from "../../core/store/reducers/master.slice";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { API_PREFIX_V1, TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import {
  API_GET_ALL_ACCOUNTS_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_DEAL_STAGE_ACTIVE,
  API_GET_DEAL_SUB_STAGE_BY_STAGE,
  API_GET_SALES_FILTERED,
  API_LOOKUP_FOREX_RATE,
  API_GET_DOCUMENT_SIGNED_URL,
  ROUTE_MANAGE_FOREX,
  API_GET_SALES_TABLE_PREFERENCES,
  API_SAVE_SALES_TABLE_PREFERENCES,
  ROUTE_SALES_LOST,
  ROUTE_SALES_WON,
  API_GET_SALES_LIST,
} from "../../shared/routes/private.routes";
import { RootState } from "../../shared/types/common";
import {
  accessAllowed,
  formatCost1,
  showNotification,
} from "../../shared/utils/utils";
import { useNavigate } from "react-router-dom";

const SALES_PAGE_SIZE = 15;
const SALES_FOREX_DRAFT_KEY = "sales-forex-draft";

const formatClosureQuarter = (value: string) => {
  if (!value) return "";
  const fiscal = /^(\d{4})-(\d{2}|\d{4})-Q([1-4])$/.exec(value);
  if (fiscal) return `FY${fiscal[2].slice(-2)}-Q${fiscal[3]}`;
  const calendar = /^(\d{4})-Q([1-4])$/.exec(value);
  return calendar ? `FY${calendar[1].slice(-2)}-Q${calendar[2]}` : value;
};

const compareOptionLabels = (a: { label?: string }, b: { label?: string }) =>
  String(a.label || "").localeCompare(String(b.label || ""), "en", { sensitivity: "base" });

const DEFAULT_COLUMNS = [
  "dealReference",
  "entityName",
  "clientName",
  "opportunity",
  "dealStageName",
  "dealValue",
  "dealValueTarget",

];

type WorkflowStatus = "PIPELINE" | "WON" | "LOST";

interface EntityOption {
  id: string | number;
  name: string;
}

interface ProjectTypeOption {
  id: string | number;
  name: string;
}

interface CurrencyOption {
  id: string | number;
  name: string;
}

interface AccountOption {
  entityId?: string | number;
  accountId: string | number;
  accountName: string;
}

interface LeadOwnerFilterOption {
  entityId?: string | number;
  leadOwnerId: string | number;
  leadOwnerName: string;
}

interface DeliveryOwnerFilterOption {
  deliveryOwnerId: string | number;
  deliveryOwnerName: string;
}

interface SalesFilterOptions {
  leadOwners: LeadOwnerFilterOption[];
  deliveryOwners: DeliveryOwnerFilterOption[];
  closureMonths: string[];
  closureQuarters: { value: string; label: string }[];
  opportunities: { value: string; label: string }[];
}

interface StaffOption {
  staffId: string | number;
  employeeId?: string | number;
  staffName: string;
}

const ManageSalesPage: React.FC<{ workflowStatus?: WorkflowStatus }> = ({ workflowStatus = "PIPELINE" }) => {
  const viewName = workflowStatus === "PIPELINE" ? "Pipeline" : workflowStatus === "WON" ? "Won" : "Lost";
  const salesPreferenceKey =
    workflowStatus === "WON"
      ? "sales-won"
      : workflowStatus === "LOST"
        ? "sales-lost"
        : "sales-pipeline";
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const { loadingSales, salesList, pagination } = useSelector(
    (state: RootState) => state.sales
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [formOpen, setFormOpen] = useState(false);
  const [logRecord, setLogRecord] = useState<any>(null);
  const [conversionRecord, setConversionRecord] = useState<any>(null);
  const [convertingTo, setConvertingTo] = useState<"WON" | "LOST" | null>(null);
  const [generatingQuotation, setGeneratingQuotation] = useState(false);
  const quotationRequest = useRef(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  useEffect(() => {
    quotationRequest.current++;
    setGeneratingQuotation(false);
    if (workflowStatus === "WON") {
      const wonStage = stages.find(
        (stage) => stage.name?.trim().toUpperCase().includes("WON")
      );

      if (wonStage) {
        form.setFieldValue("dealStageId", wonStage.id);
      }
    }
  }, [formOpen, selectedEntity]);
  const [pageSize, setPageSize] = useState(SALES_PAGE_SIZE);
  const [filters, setFilters] = useState<any>({});
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });
  const [entityList, setEntityList] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<any>({
    leadOwners: [],
    deliveryOwners: [],
    closureMonths: [],
    closureQuarters: [],
    opportunities: [],
  });
  const closureDate = Form.useWatch("estimatedClosureDate", form);
  const closureEntityId = Form.useWatch("entityId", form);
  const closureEntity = entityList.find((entity) => entity.id === closureEntityId);
  const closureQuarter = (() => {
    if (!closureDate || !closureEntity) return "";
    const date = dayjs(closureDate);
    if (!date.isValid()) return "";
    const month = date.month();
    const year = date.year();
    const startYear = month < 3 ? year - 1 : year;
    return `FY${String((startYear + 1) % 100).padStart(2, "0")}-Q${Math.floor(((month + 9) % 12) / 3) + 1}`;
  })();

  const closureMonth = (() => {
    if (!closureDate) return "";

    const date = dayjs(closureDate);

    if (!date.isValid()) return "";

    return date.format("MMMM");
  })();
  const [projectTypes, setProjectTypes] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [referenceReady, setReferenceReady] = useState(false);
  const selectedEntityId = Form.useWatch("entityId", form);
  const clientOptions = accounts
    .filter((account) => account.entityId === selectedEntityId)
    .map((account) => ({ value: account.accountId, label: account.accountName }))
    .sort(compareOptionLabels);
  const clientfilterOptions = accounts
    .map((account) => ({ value: account.accountId, label: account.accountName }))
    .sort(compareOptionLabels);
  const [staff, setStaff] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [subStages, setSubStages] = useState<any[]>([]);
  const [converted, setConverted] = useState<any>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [preferencesLoadedFor, setPreferencesLoadedFor] = useState<string | null>(null);
  const [preferencesCanSave, setPreferencesCanSave] = useState(false);
  const skipNextPreferenceSave = useRef(true);
  const preferenceRequestId = useRef(0);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  const loadList = useCallback(
    (page: any = null) => {
      const nextPage = page || pagination?.page || 1;
      dispatch(
        getSalesList({
          params: {
            page: nextPage,
            limit: pageSize,
            sort_by: sorter.sort_by || "",
            sort_type: sorter.sort_type || "DESC",
          },
          body: { ...filters, workflowStatus },
        })
      );
    },
    [dispatch, filters, pageSize, pagination?.page, sorter, workflowStatus]
  );

  useEffect(() => {
    loadList(1);
  }, [filters, pageSize, sorter, workflowStatus]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (!referenceReady) return;
    const url = new URL(window.location.href);
    const raw = sessionStorage.getItem(SALES_FOREX_DRAFT_KEY);
    if (!raw) return;
    if (url.searchParams.has("resumeForex")) {
      url.searchParams.delete("resumeForex");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
    sessionStorage.removeItem(SALES_FOREX_DRAFT_KEY);
    try {
      const draft = JSON.parse(raw);
      form.setFieldsValue({
        ...draft.values,
        receivedDate: draft.values.receivedDate ? dayjs(draft.values.receivedDate) : null,
        estimatedClosureDate: draft.values.estimatedClosureDate ? dayjs(draft.values.estimatedClosureDate) : null,
      });
      setSelectedEntity(draft.salesId ? { salesId: draft.salesId } : null);
      if (draft.values.dealStageId) loadSubStages(draft.values.dealStageId);
      setFormOpen(true);
      if (draft.values.dealCurrencyId && draft.values.targetCurrencyId && draft.values.dealValue) {
        lookupConversion();
      }
    } catch {
      showNotification("Unable to restore the Sales draft", "error");
    }
  }, [referenceReady]);

  const fetchReferenceData = async () => {
    const [entities, types, currencyRes, accountRes, staffRes, stageRes, filterOptionsRes] =
      await Promise.all([
        dispatch(requestGetByModuleCommon({ module: "master_entity" })).unwrap(),
        dispatch(requestGetByModuleCommon({ module: "master_project_type" })).unwrap(),
        dispatch(requestGetByModuleCommon({ module: "master_currency" })).unwrap(),
        Client.get(API_GET_ALL_ACCOUNTS_LIST),
        Client.get(API_GET_ALL_STAFF_LIST),
        Client.get(API_GET_DEAL_STAGE_ACTIVE),
        Client.get(API_GET_SALES_FILTERED),
      ]);
    setEntityList(entities?.data?.items || []);
    setProjectTypes(types?.data?.items || []);
    setCurrencies(currencyRes?.data?.items || []);
    setAccounts(accountRes?.data?.data || []);
    setStaff(staffRes?.data?.data || []);
    setStages(stageRes?.data?.data || []);
    setFilterOptions(filterOptionsRes?.data?.data || {});
    setReferenceReady(true);
    console.log(filterOptionsRes?.data?.data, "filterOptionsRes?.data");
  };

  const currencyOptions = currencies.map((currency) => ({
    value: currency.id,
    label: currency.name,
  })).sort(compareOptionLabels);

  const staffOptions = staff.map((item) => ({
    value: item.staffId,
    label: item.employeeId ? `${item.staffName} (${item.employeeId})` : item.staffName,
  })).sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" })
  );

  const loadSubStages = async (dealStageId: string) => {
    if (!dealStageId) {
      setSubStages([]);
      return;
    }
    const response = await Client.get(API_GET_DEAL_SUB_STAGE_BY_STAGE + dealStageId);
    setSubStages(response?.data?.data || []);
  };

  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [wonForm] = Form.useForm();
  const [wonAttachment, setWonAttachment] = useState<File | null>(null);
  const [submittingWon, setSubmittingWon] = useState(false);
  const [salesAttachments, setSalesAttachments] = useState<File[]>([]);
  const [existingSalesAttachments, setExistingSalesAttachments] = useState<any[]>([]);
  const [uploadingSalesAttachment, setUploadingSalesAttachment] = useState(false);
  const navigate = useNavigate();
  const handleConvertToWon = () => {
    wonForm.resetFields();
    setWonAttachment(null);
    setWonModalOpen(true);
  };

  const handleWonSubmit = async (values: any) => {
    const salesId = conversionRecord?.salesId;
    if (!salesId) {
      showNotification("Sales ID not found", "error");
      return;
    }

    const { estimatedStartDate, estimatedEndDate } = values;
    let durationMonths = 0;

    if (estimatedStartDate && estimatedEndDate) {
      durationMonths = Math.ceil(
        dayjs(estimatedEndDate).diff(dayjs(estimatedStartDate), "month", true)
      );
    }

    const payload = {
      ...values,
      closeDate: values.closeDate ? dayjs(values.closeDate).format("YYYY-MM-DD") : null,
      estimatedStartDate: estimatedStartDate ? dayjs(estimatedStartDate).format("YYYY-MM-DD") : null,
      estimatedEndDate: estimatedEndDate ? dayjs(estimatedEndDate).format("YYYY-MM-DD") : null,
      durationMonths,
      addInYear: values.addInYear || "FY 2026-27",
      currentStatus: workflowStatus,
      targetStatus: "WON",
    };

    setSubmittingWon(true);

    try {
      // Step 1: Convert the opportunity to WON first.
      const convertedSuccessfully = await convertSales("WON", payload);

      if (!convertedSuccessfully) {
        return;
      }

      // Step 2: Upload the attachment only after conversion succeeds.
      if (wonAttachment) {
        const formData = new FormData();
        formData.append("file", wonAttachment);

        const response = await Client.post(
          `${API_PREFIX_V1}sales/${salesId}/attachment`,
          formData
        );

        if (response?.status < 200 || response?.status >= 300) {
          showNotification(
            "Opportunity converted to WON, but the attachment upload failed. Please upload the attachment from the WON record.",
            "error"
          );
          return;
        }
      }

      // Step 3: Close and reset only after conversion and upload succeed.
      setWonModalOpen(false);
      wonForm.resetFields();
      setWonAttachment(null);
    } catch (error: any) {
      showNotification(
        error?.response?.data?.message ||
        "Opportunity converted, but the attachment upload failed. Please check the WON record.",
        "error"
      );
    } finally {
      setSubmittingWon(false);
    }

  };
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [lostForm] = Form.useForm();

  const handleConvertToLost = () => {
    lostForm.resetFields();
    const lostStage = stages.find(
      (stage) => stage.name?.trim().toUpperCase().includes("LOST")
    );

    if (lostStage) {
      lostForm.setFieldsValue({
        dealStageId: lostStage.id,
      });

      // Load only LOST sub-stages
      loadSubStages(lostStage.id);
    }
    setLostModalOpen(true);
  };

  const handleLostSubmit = async (values: any) => {
    const salesId = conversionRecord?.salesId;

    if (!salesId) {
      message.error("Sales ID not found");
      return;
    }

    const payload = {
      lostDate: values.lostDate
        ? dayjs(values.lostDate).format("YYYY-MM-DD")
        : null,
      dealStageId: values.dealStageId,
      dealSubStageId: values.dealSubStageId,
    };

    console.log(payload, "payload");
    await convertSales(
      "LOST",
      payload
    );

    setLostModalOpen(false);
    lostForm.resetFields();
  };

  const lookupConversion = async () => {
    const values = form.getFieldsValue();
    if (!values.dealCurrencyId || !values.targetCurrencyId || !values.dealValue) {
      setConverted(null);
      return;
    }
    const month = dayjs().format("YYYY-MM");
    const response = await Client.get(API_LOOKUP_FOREX_RATE, {
      params: {
        from: values.dealCurrencyId,
        to: values.targetCurrencyId,
        month,
      },
    });
    if (response?.data?.success) {
      const rate = Number(response.data.data.rate);
      setConverted({
        rate,
        value: Number(values.dealValue) * rate,
        month: response.data.data.month,
      });
      return;
    }
    setConverted(null);
    const fromName = currencies.find((c) => c.id === values.dealCurrencyId)?.name;
    const toName = currencies.find((c) => c.id === values.targetCurrencyId)?.name;
    Modal.confirm({
      title: "Conversion rate missing",
      content: `No conversion rate is configured for ${fromName} -> ${toName} for ${dayjs().format("MMM-YY")}. Do you want to configure it now?`,
      okText: accessAllowed(myPermissions, allPermissions.CREATE_FOREX)
        ? "Configure"
        : "OK",
      cancelText: "Cancel",
      onOk: () => {
        if (accessAllowed(myPermissions, allPermissions.CREATE_FOREX)) {
          const draftValues = form.getFieldsValue(true);
          try {
            sessionStorage.setItem(SALES_FOREX_DRAFT_KEY, JSON.stringify({
              salesId: selectedEntity?.salesId,
              values: {
                ...draftValues,
                receivedDate: draftValues.receivedDate?.format("YYYY-MM-DD"),
                estimatedClosureDate: draftValues.estimatedClosureDate?.format("YYYY-MM-DD"),
              },
            }));
            window.location.href = `${ROUTE_MANAGE_FOREX}?from=${encodeURIComponent(values.dealCurrencyId)}&to=${encodeURIComponent(values.targetCurrencyId)}&month=${month}&returnToSales=1`;
          } catch {
            showNotification("Unable to keep the Sales draft in this browser tab", "error");
          }
        }
      },
    });
  };
  const loadExistingSalesAttachment = async (salesId?: string) => {
    if (!salesId) {
      setExistingSalesAttachments([]);
      return;
    }

    try {
      const response = await Client.get(
        `${API_PREFIX_V1}documents/list?module=SALES&parentId=${encodeURIComponent(
          salesId
        )}&status=ACTIVE&limit=10&sort_by=updatedDate&sort_type=DESC`
      );

      // Support both the direct and wrapped API response shapes.
      const result =
        response?.data?.data?.data ??
        response?.data?.data ??
        [];

      const documents = Array.isArray(result) ? result : [];

      setExistingSalesAttachments(documents);
    } catch (error) {
      console.error("Failed to load Sales attachment:", error);
      setExistingSalesAttachments([]);

      showNotification(
        "Unable to load the existing PO/Contract attachment",
        "error"
      );
    }
  };
  const handleSalesAttachmentPreview = async (file: any) => {
    const documentId = file?.uid;

    const document = existingSalesAttachments.find(
      (attachment) => String(attachment.id) === String(documentId)
    );

    if (!documentId || !document) return;

    try {
      const response = await Client.get(
        `${API_GET_DOCUMENT_SIGNED_URL}${documentId}`
      );

      const downloadUrl = response?.data?.data?.url;

      if (!downloadUrl) {
        showNotification("Unable to generate the attachment download URL", "error");
        return;
      }

      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      showNotification(
        error?.response?.data?.message ||
        "Unable to download the PO/Contract attachment",
        "error"
      );
    }
  };

  const openCreate = () => {
    setSalesAttachments([]);
    setExistingSalesAttachments([]);
    setSelectedEntity(null);
    setConverted(null);
    form.resetFields();
    form.setFieldsValue({ targetCurrencyId: currencies.find((c) => c.name === "MYR")?.id });
    setFormOpen(true);
  };

  const openEdit = async (record: any) => {
    setSalesAttachments([]);
    setExistingSalesAttachments([]);
    setSelectedEntity(record);
    await loadExistingSalesAttachment(record?.salesId);
    console.log(record, "record");
    await loadSubStages(record.dealStageId);
    setConverted({
      rate: Number(record.conversionRateUsed),
      value: Number(record.dealValueTarget),
      month: record.conversionRateMonth,
    });
    form.setFieldsValue({
      ...record,
      dealValue: Number(record.dealValue),
      grossMargin:
        record.grossMargin === null || record.grossMargin === undefined
          ? undefined
          : Number(record.grossMargin),
      probability:
        record.probability === null || record.probability === undefined
          ? undefined
          : Number(record.probability),
      receivedDate: record.receivedDate ? dayjs(record.receivedDate) : null,
      estimatedClosureDate: record.estimatedClosureDate
        ? dayjs(record.estimatedClosureDate)
        : null,
      closeDate: record.closeDate ? dayjs(record.closeDate) : null,
      estimatedStartDate: record.estimatedStartDate
        ? dayjs(record.estimatedStartDate)
        : null,
      estimatedEndDate: record.estimatedEndDate
        ? dayjs(record.estimatedEndDate)
        : null,
      lostDate: record.lostDate ? dayjs(record.lostDate) : null,
      sowpoNumber: record.sowpoNumber || "",
      durationMonths: record.durationMonths || 0,
      addInYear: record.addInYear || "",
    });
    setFormOpen(true);
  };

  const deleteSales = (record: any) => {
    Modal.confirm({
      title: "Delete sales record",
      content: "Are you sure you want to delete this sales record?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await dispatch(deleteSalesById(record.salesId));
        if (result?.payload?.data?.statusCode === 200) {
          showNotification("Sales record deleted successfully", "success");
          loadList();
        } else {
          showNotification("Unable to delete sales record", "error");
        }
      },
    });
  };

  const generateQuotationNumber = async () => {
    if (generatingQuotation) return;
    const request = ++quotationRequest.current;
    setGeneratingQuotation(true);
    try {
      const response = await Client.post(`${API_PREFIX_V1}sales/quotation-number`, {}, { shouldHide: true });
      if (request !== quotationRequest.current) return;
      if (response?.status >= 200 && response.status < 300 && response.data?.data?.quotationNumber) {
        form.setFieldValue("quotationSowNumber", response.data.data.quotationNumber);
      } else {
        showNotification("Unable to generate quotation number", "error");
      }
    } catch {
      if (request === quotationRequest.current) showNotification("Unable to generate quotation number", "error");
    } finally {
      if (request === quotationRequest.current) setGeneratingQuotation(false);
    }
  };


  const saveSales = async (values: any) => {
    if (generatingQuotation || uploadingSalesAttachment) return;

    const toOptionalNumber = (value: any) => {
      if (value === undefined || value === null || value === "") return undefined;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const payload = {
      ...values,
      receivedDate: values.receivedDate?.format("YYYY-MM-DD"),
      estimatedClosureDate: values.estimatedClosureDate?.format("YYYY-MM-DD"),
      dealValue: Number(values.dealValue),
      workflowStatus,
      grossMargin: toOptionalNumber(values.grossMargin),
      probability: toOptionalNumber(values.probability),
    };

    setUploadingSalesAttachment(true);

    try {
      let salesId = selectedEntity?.salesId;

      // Save the sales record first so we have its ID for the attachment.
      const result = await dispatch(
        selectedEntity
          ? updateSalesById({ salesId, body: payload })
          : createSales(payload)
      );

      if (result?.payload?.data?.statusCode !== 200) {
        showNotification("Unable to save sales record", "error");
        return;
      }

      // For a newly created record, get its ID from the create response.
      salesId =
        salesId ||
        result?.payload?.data?.data?.salesId ||
        result?.payload?.data?.salesId;

      if (salesAttachments.length > 0 && !salesId) {
        throw new Error("Sales record saved, but its ID was not returned.");
      }

      for (const file of salesAttachments) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await Client.post(
          `${API_PREFIX_V1}sales/${salesId}/attachment`,
          formData
        );

        if (response?.status < 200 || response?.status >= 300) {
          throw new Error(`Failed to upload ${file.name}`);
        }
      }
      showNotification("Sales record saved successfully", "success");
      setFormOpen(false);
      setSalesAttachments([]);
      setExistingSalesAttachments([]);
      loadList();
    } catch (error: any) {
      showNotification(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to save sales record or upload attachment",
        "error"
      );
    } finally {
      setUploadingSalesAttachment(false);
    }
  };
  const convertedValueText = () => {
    if (!converted) return "";
    const value = Number(converted.value);
    const rate = Number(converted.rate);
    if (!Number.isFinite(value) || !Number.isFinite(rate)) return "";
    return value.toFixed(2);
  };

  const findTargetStage = (targetStatus: "WON" | "LOST") => {
    const stageName = targetStatus === "WON" ? "Won" : "Lost";
    const matchedStage = stages.find((stage) =>
      String(stage?.name || "").toLowerCase() === stageName.toLowerCase()
    );
    return matchedStage || stages.find((stage) =>
      String(stage?.name || "").toLowerCase().includes(targetStatus.toLowerCase())) || null;
  };

  const convertedRate = converted ? Number(converted.rate) : NaN;
  const targetValueLabel = Number.isFinite(convertedRate)
    ? `Deal Value (Reporting) @ ${convertedRate}`
    : "Deal Value (Reporting)";

  const convertSales = async (
    targetStatus: "WON" | "LOST",
    payload: any
  ): Promise<boolean> => {
    if (!conversionRecord || convertingTo) return false;
    const targetName = targetStatus === "WON" ? "Won" : "Lost";
    setConvertingTo(targetStatus);
    try {
      const response = await Client.put(API_PREFIX_V1 + 'sales/convert/' + conversionRecord.salesId, {
        currentStatus: workflowStatus,
        targetStatus,
        ...payload,
      });
      if (response?.data?.statusCode !== 200) {
        throw new Error(response?.data?.message || 'Unable to convert opportunity. Refresh and retry.');
      }
      showNotification('Opportunity moved to ' + targetName, "success");
      setConversionRecord(null);
      // Redirect based on the target status after API success
      if (targetStatus === "WON") {
        navigate(ROUTE_SALES_WON);
      } else if (targetStatus === "LOST") {
        navigate(ROUTE_SALES_LOST);
      }
      loadList(1);
      return true;
    } catch (error: any) {
      showNotification(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to convert opportunity. Refresh and retry.",
        "error"
      );
      return false;
    } finally {
      setConvertingTo(null);
    }
  };

  const exportSales = async () => {

    const response = await Client.post(
      API_GET_SALES_LIST,
      { ...filters, workflowStatus },
      {
        params: {
          sort_by: sorter.sort_by || "updatedDate",
          sort_type: sorter.sort_type || "DESC",
        },
      }
    );

    const allSales = response?.data?.data ?? [];
    console.log("sales export", allSales)
    const rows = allSales.map((item: any) => ({
      "Deal Ref": item.dealReference,
      Entity: item.entityName,
      "Received Date": item.receivedDate
        ? dayjs(item.receivedDate).format("DD-MMM-YYYY")
        : "",
      Client: item.clientName,
      ClientCategory: item.clientCategory,
      location: item.clientLocation,
      opportunityType: item.projectTypeName,
      Opportunity: item.opportunity,
      "Lead Owner": item.leadOwnerName,
      "Delivery Owner": item.deliveryOwnerName,
      'Est. Closure': item.estimatedClosureDate
        ? dayjs(item.estimatedClosureDate).format("DD-MMM-YYYY")
        : "",
      "Est. Start Date": item.estimatedStartDate
        ? dayjs(item.estimatedStartDate).format("DD-MMM-YYYY")
        : "",
      "Est. End Date": item.estimatedEndDate
        ? dayjs(item.estimatedEndDate).format("DD-MMM-YYYY")
        : "",
      "Lost Date": item.lostDate
        ? dayjs(item.lostDate).format("DD-MMM-YYYY")
        : "",
      "Close Date": item.closeDate
        ? dayjs(item.closeDate).format("DD-MMM-YYYY")
        : "",
      "addInYear": item.addInYear,
      "SOW/PO Number": item.sowpoNumber,
      "durationMonths": item.durationMonths,
      "closureQuarter": item.closureQuarter,
      "closureMonth": item.closureMonth,
      Stage: item.dealStageName,
      "Sub Stage": item.dealSubStageName,
      "Deal Value": item.dealValue,
      "Deal Currency": item.dealCurrencyName,
      "Deal Value (Reporting)": item.dealValueTarget,
      "Reporting Currency": item.targetCurrencyName,
      "Probability (%)": item.probability,
      Priority: item.priority,
      remarks: item.remarks,
      "Gross Margin (%)": item.grossMargin,
      "Duration (Months)": item.durationMonths,
      "Quotation/SOW Number": item.quotationSowNumber,
      "New Or Renewal": item.newRenewal,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", viewName.toLowerCase() + "-sales.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const calculateDuration = () => {
    const startDate = form.getFieldValue("estimatedStartDate");
    const endDate = form.getFieldValue("estimatedEndDate");

    if (startDate && endDate) {
      const months = endDate.diff(startDate, "month", true);

      form.setFieldValue(
        "durationMonths",
        Math.max(0, Math.round(months))
      );
    } else {
      form.setFieldValue("durationMonths", undefined);
    }
  };

  const handleSaveSales = async (values: any) => {
    if (
      selectedEntity?.workflowStatus === "WON" ||
      selectedEntity?.workflowStatus === "LOST"
    ) {
      Modal.confirm({
        title: "Confirm Update",
        content:
          "This record was directly created or converted from the Pipeline. Do you want to update this Pipeline data?",
        okText: "Confirm & Update",
        cancelText: "Cancel",
        centered: true,
        onOk: async () => {
          await saveSales(values);
        },
      });

      return;
    }

    await saveSales(values);
  };

  const allColumns = useMemo(
    () => [
      { title: "Deal Ref", dataIndex: "dealReference", key: "dealReference", ...getSortableColumnProps("dealReference") },
      { title: "Entity", dataIndex: "entityName", key: "entityName", ...getSortableColumnProps("entityName") },
      { title: "Sale Received Date", dataIndex: "receivedDate", key: "receivedDate", ...getSortableColumnProps("receivedDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Client", dataIndex: "clientName", key: "clientName", ...getSortableColumnProps("clientName") },
      { title: "Client Category", dataIndex: "clientCategory", key: "clientCategory", ...getSortableColumnProps("clientCategory") },
      { title: "Location", dataIndex: "clientLocation", key: "clientLocation", ...getSortableColumnProps("clientLocation") },
      { title: "Opportunity Type", dataIndex: "projectTypeName", key: "projectTypeName", ...getSortableColumnProps("projectTypeName") },
      { title: "Opportunity Description", dataIndex: "opportunity", key: "opportunity", ...getSortableColumnProps("opportunity") },
      { title: "Lead Owner", dataIndex: "leadOwnerName", key: "leadOwnerName", ...getSortableColumnProps("leadOwnerName") },
      { title: "Delivery Owner", dataIndex: "deliveryOwnerName", key: "deliveryOwnerName", ...getSortableColumnProps("deliveryOwnerName") },
      { title: "Est. Closure", dataIndex: "estimatedClosureDate", key: "estimatedClosureDate", ...getSortableColumnProps("estimatedClosureDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Est. Start Date", dataIndex: "estimatedStartDate", key: "estimatedStartDate", ...getSortableColumnProps("estimatedStartDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Est. End Date", dataIndex: "estimatedEndDate", key: "estimatedEndDate", ...getSortableColumnProps("estimatedEndDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Lost Date", dataIndex: "lostDate", key: "lostDate", ...getSortableColumnProps("lostDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Close Date", dataIndex: "closeDate", key: "closeDate", ...getSortableColumnProps("closeDate"), render: (v: string) => v ? dayjs(v).format("DD-MMM-YYYY") : "" },
      { title: "Year Revenue", dataIndex: "addInYear", key: "addInYear", ...getSortableColumnProps("addInYear") },
      { title: "SOW/PO #", dataIndex: "sowPoNumber", key: "sowPoNumber", ...getSortableColumnProps("sowPoNumber") },
      { title: "DurationMonths", dataIndex: "durationMonths", key: "durationMonths", ...getSortableColumnProps("durationMonths"), render: (v: number) => v || 0 },
      { title: "Closure Qtr", dataIndex: "closureQuarter", key: "closureQuarter", ...getSortableColumnProps("closureQuarter"), render: formatClosureQuarter },
      { title: "Closure Month", dataIndex: "closureMonth", key: "closureMonth", ...getSortableColumnProps("closureMonth"), render: (v: string) => v || "" },
      { title: "Stage", dataIndex: "dealStageName", key: "dealStageName", ...getSortableColumnProps("dealStageName") },
      { title: "Sub-Stage", dataIndex: "dealSubStageName", key: "dealSubStageName", ...getSortableColumnProps("dealSubStageName") },
      { title: "Deal Value", dataIndex: "dealValue", key: "dealValue", ...getSortableColumnProps("dealValue"), render: (v: number, r: any) => formatCost1(v, r.dealCurrencyName) },
      { title: "Deal Value (Reporting)", dataIndex: "dealValueTarget", key: "dealValueTarget", ...getSortableColumnProps("dealValueTarget"), render: (v: number, r: any) => formatCost1(v, r.targetCurrencyName) },
      { title: "Gross Margin (%)", dataIndex: "grossMargin", key: "grossMargin", ...getSortableColumnProps("grossMargin"), render: (v: any) => v === null || v === undefined || v === "" ? "" : `${v}%` },
      { title: "Probability (%)", dataIndex: "probability", key: "probability", ...getSortableColumnProps("probability"), render: (v: any) => v === null || v === undefined || v === "" ? "" : `${v}%` },
      { title: "SOW #", dataIndex: "quotationSowNumber", key: "quotationSowNumber", ...getSortableColumnProps("quotationSowNumber") },
      { title: "Priority", dataIndex: "priority", key: "priority", ...getSortableColumnProps("priority") },
      { title: "New/Renewal", dataIndex: "newRenewal", key: "newRenewal", ...getSortableColumnProps("newRenewal") },
      { title: "Remarks", dataIndex: "remarks", key: "remarks", ...getSortableColumnProps("remarks") },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 230,
        align: "center",
        render: (_: any, record: any) =>
          accessAllowed(myPermissions, allPermissions.UPDATE_SALES) ||
            accessAllowed(myPermissions, allPermissions.VIEW_SALES) ||
            accessAllowed(myPermissions, allPermissions.DELETE_SALES) ? (
            <Space>

              {/* {accessAllowed(myPermissions, allPermissions.UPDATE_SALES) && (
                <Button
                  size="small"
                  icon={workflowStatus === "WON" ? <CheckCircleOutlined /> : workflowStatus === "LOST" ? <CloseCircleOutlined /> : <SwapOutlined />}
                  title="Convert opportunity"
                  aria-label="Convert opportunity"
                  onClick={() => setConversionRecord(record)}
                  style={{
                    color: workflowStatus === "WON" ? "#15803d" : workflowStatus === "LOST" ? "#b91c1c" : "#6554e8",
                    background: workflowStatus === "WON" ? "#f0fdf4" : workflowStatus === "LOST" ? "#fef2f2" : "#f3f0ff",
                    borderColor: workflowStatus === "WON" ? "#bbf7d0" : workflowStatus === "LOST" ? "#fecaca" : "#ddd6fe",
                    borderRadius: 6, fontWeight: 500,
                  }}
                >{workflowStatus === "PIPELINE" ? "Convert" : viewName}</Button>
              )} */}
              {workflowStatus === "PIPELINE" &&
                accessAllowed(myPermissions, allPermissions.UPDATE_SALES) && (
                  <Button
                    size="small"
                    icon={<SwapOutlined />}
                    title="Convert opportunity"
                    aria-label="Convert opportunity"
                    onClick={() => setConversionRecord(record)}
                    style={{
                      color: "#6554e8",
                      background: "#f3f0ff",
                      borderColor: "#ddd6fe",
                      borderRadius: 6,
                      fontWeight: 500,
                    }}
                  >

                  </Button>
                )}
              {accessAllowed(myPermissions, allPermissions.UPDATE_SALES) && (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(record)}
                  title="Edit"
                  aria-label="Edit sales record"
                />
              )}
              {accessAllowed(myPermissions, allPermissions.VIEW_SALES) && (
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  title="View activity log"
                  aria-label="View activity log"
                  onClick={() => setLogRecord(record)}
                />
              )}
              {/* {accessAllowed(myPermissions, allPermissions.DELETE_SALES) && (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => deleteSales(record)}
                  title="Delete"
                  aria-label="Delete sales record"
                />
              )} */}
            </Space>
          ) : null,
      },
    ],
    [myPermissions, salesList, sorter, workflowStatus, loadList]
  );
  // Load the saved column preferences for the active Sales page.
  useEffect(() => {
    const requestId = ++preferenceRequestId.current;
    let cancelled = false;

    skipNextPreferenceSave.current = true;
    setPreferencesLoadedFor(null);
    setPreferencesCanSave(false);

    const fetchSalesTablePreferences = async () => {
      try {
        const response = await Client.get(
          API_GET_SALES_TABLE_PREFERENCES(salesPreferenceKey)
        );

        if (cancelled || requestId !== preferenceRequestId.current) {
          return;
        }

        const savedPreferences = response?.data?.data;
        const validColumnKeys = new Set(
          allColumns
            .filter((column: any) => column.key !== "actions")
            .map((column: any) => String(column.key))
        );

        if (Array.isArray(savedPreferences?.visibleColumns)) {
          const validColumns = savedPreferences.visibleColumns.filter(
            (key: unknown): key is string =>
              typeof key === "string" && validColumnKeys.has(key)
          );

          setVisibleColumns(
            validColumns.length > 0
              ? validColumns
              : [...DEFAULT_COLUMNS]
          );
        } else {
          setVisibleColumns([...DEFAULT_COLUMNS]);
        }

        setPreferencesCanSave(true);
        setPreferencesLoadedFor(salesPreferenceKey);
      } catch (error) {
        if (!cancelled && requestId === preferenceRequestId.current) {
          console.error(
            "Failed to load Sales table preferences:",
            error
          );
        }
      }
    };

    void fetchSalesTablePreferences();

    return () => {
      cancelled = true;
    };
  }, [salesPreferenceKey, allColumns]);

  // Save preferences only after the active page's settings have loaded.
  useEffect(() => {
    if (
      preferencesLoadedFor !== salesPreferenceKey ||
      !preferencesCanSave
    ) {
      return;
    }

    // Prevent saving the initial loaded settings back unnecessarily.
    if (skipNextPreferenceSave.current) {
      skipNextPreferenceSave.current = false;
      return;
    }

    const saveSalesTablePreferences = async () => {
      try {
        await Client.put(
          API_SAVE_SALES_TABLE_PREFERENCES(salesPreferenceKey),
          {
            preferences: {
              visibleColumns,
            },
          }
        );
      } catch (error) {
        console.error(
          "Failed to save Sales table preferences:",
          error
        );
      }
    };

    void saveSalesTablePreferences();
  }, [
    visibleColumns,
    salesPreferenceKey,
    preferencesLoadedFor,
    preferencesCanSave,
  ]);

  const columns = allColumns.filter(
    (column: any) => column.key === "actions" || visibleColumns.includes(column.key)
  );

  return (
    <div className="app-page">
      {logRecord && (
        <Drawer title="Log Details" open onClose={() => setLogRecord(null)}>
          <ActivityLogComponent key={logRecord.salesId} id={logRecord.salesId} record={logRecord} module="Sales"
            referenceNames={{
              entityId: Object.fromEntries(entityList.map((item) => [item.id, item.name])),
              clientId: Object.fromEntries(accounts.map((item) => [item.accountId, item.accountName])),
              projectTypeId: Object.fromEntries(projectTypes.map((item) => [item.id, item.name])),
              leadOwnerId: Object.fromEntries(staff.map((item) => [item.staffId, item.staffName])),
              deliveryOwnerId: Object.fromEntries(staff.map((item) => [item.staffId, item.staffName])),
              dealStageId: Object.fromEntries(stages.map((item) => [item.id, item.name])),
              dealSubStageId: Object.fromEntries(subStages.map((item) => [item.id, item.name])),
              dealCurrencyId: Object.fromEntries(currencies.map((item) => [item.id, item.name])),
              targetCurrencyId: Object.fromEntries(currencies.map((item) => [item.id, item.name])),
            }} />
        </Drawer>
      )}
      {/* <Modal
        title="Convert opportunity"
        open={!!conversionRecord}
        onCancel={() => { if (!convertingTo) setConversionRecord(null); }}
        closable={!convertingTo}
        maskClosable={!convertingTo}
        keyboard={!convertingTo}
        footer={<Space>
          <Button disabled={!!convertingTo} onClick={() => setConversionRecord(null)}>Cancel</Button>
          {workflowStatus !== "WON" && <Button
            icon={<CheckCircleOutlined />}
            loading={convertingTo === "WON"}
            disabled={!!convertingTo}
            style={{ color: "#15803d", background: "#f0fdf4", borderColor: "#bbf7d0" }}
            //onClick={() => convertSales("WON")}
            onClick={handleConvertToWon}
          >Convert to Won</Button>}
          {workflowStatus !== "LOST" && <Button
            icon={<CloseCircleOutlined />}
            loading={convertingTo === "LOST"}
            disabled={!!convertingTo}
            style={{ color: "#b91c1c", background: "#fef2f2", borderColor: "#fecaca" }}
            onClick={handleConvertToLost}
          >Convert to Lost</Button>}
        </Space>}
      >
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <strong>Client Name</strong>
            <div>{conversionRecord?.clientName || "-"}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Opportunity</strong>
            <div>{conversionRecord?.opportunity || "-"}</div>
          </div>
          <div>
            <strong>Deal Value</strong>
            <div> {conversionRecord?.dealValue
              ? formatCost1(
                conversionRecord.dealValue,
                conversionRecord.dealCurrencyName
              )
              : "-"}</div>
          </div>
        </div>
      </Modal> */}
      <Modal
        title="Convert opportunity"
        open={!!conversionRecord}
        onCancel={() => {
          if (!convertingTo) setConversionRecord(null);
        }}
        closable={!convertingTo}
        maskClosable={!convertingTo}
        keyboard={!convertingTo}
        footer={
          <Space>
            <Button
              disabled={!!convertingTo}
              onClick={() => setConversionRecord(null)}
            >
              Cancel
            </Button>

            {workflowStatus !== "WON" && (
              <Button
                icon={<CheckCircleOutlined />}
                loading={convertingTo === "WON"}
                disabled={!!convertingTo}
                style={{
                  color: "#15803d",
                  background: "#f0fdf4",
                  borderColor: "#bbf7d0",
                }}
                onClick={handleConvertToWon}
              >
                Convert to Won
              </Button>
            )}


            {workflowStatus !== "LOST" && (
              <Button
                icon={<CloseCircleOutlined />}
                loading={convertingTo === "LOST"}
                disabled={!!convertingTo}
                style={{
                  color: "#b91c1c",
                  background: "#fef2f2",
                  borderColor: "#fecaca",
                }}
                onClick={handleConvertToLost}
              >
                Convert to Lost
              </Button>
            )}
          </Space>
        }
      >
        <div style={{ marginTop: 20 }}>
          <Row gutter={[24, 20]}>
            <Col xs={24} sm={12}>
              <div>
                <strong>Entity</strong>
                <div>{conversionRecord?.entityName || "-"}</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <strong>Client Name</strong>
                <div>{conversionRecord?.clientName || "-"}</div>
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div>
                <strong>Opportunity Description</strong>
                <div>{conversionRecord?.opportunity || "-"}</div>
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div>
                <strong>Deal Value</strong>
                <div>
                  {conversionRecord?.dealValue
                    ? formatCost1(
                      conversionRecord.dealValue,
                      conversionRecord.dealCurrencyName,
                    )
                    : "-"}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
      {bulkOpen && <SalesBulkUpload onClose={() => setBulkOpen(false)} onImported={() => loadList(1)} />}
      <Modal title="Customize Table View" open={columnSettingsOpen} onCancel={() => setColumnSettingsOpen(false)}
        width={560} footer={<Space>
          <Button onClick={() => setVisibleColumns(DEFAULT_COLUMNS)}>Reset to Default</Button>
          <Button type="primary" onClick={() => setColumnSettingsOpen(false)}>Done</Button>
        </Space>}>
        <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" }}>
          <Row gutter={[24, 0]} style={{ margin: 0 }}>
            {allColumns.filter((column) => column.key !== "actions").map((column) => {
              const checked = visibleColumns.includes(column.key);
              return <Col xs={24} sm={12} key={column.key}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 0", borderBottom: "1px solid #e2e8f0" }}>
                  <span>{column.title}</span>
                  <Switch size="small" checked={checked} aria-label={String(column.title)} disabled={checked && visibleColumns.length === 1}
                    onChange={(enabled) => setVisibleColumns((previous) => enabled
                      ? [...previous, column.key]
                      : previous.filter((key) => key !== column.key))} />
                </div>
              </Col>;
            })}
          </Row>
        </div>
      </Modal>
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                {workflowStatus === "WON" ? (
                  <TrophyOutlined />
                ) : workflowStatus === "LOST" ? (
                  <CloseCircleOutlined />
                ) : (
                  <FunnelPlotOutlined />
                )}

              </div>


              <h1 className="app-page-title">{viewName}</h1>
            </div>
            <p className="app-page-subtitle">View and manage {viewName.toLowerCase()} sales records</p>
          </Col>
          <Col>
            <Space className="app-page-actions">
              <Tooltip title="Customize Table View">
                <Button icon={<TableOutlined />} aria-label="Customize Table View" onClick={() => setColumnSettingsOpen(true)} />
              </Tooltip>
              <Button hidden={workflowStatus !== "LOST"} icon={<UploadOutlined />} disabled={!accessAllowed(myPermissions, allPermissions.SALES_BULK_CREATE)} onClick={() => setBulkOpen(true)}>
                Bulk Upload
              </Button>
              <Button icon={<DownloadOutlined />} onClick={exportSales}>
                Export
              </Button>
              {(workflowStatus === "PIPELINE" || workflowStatus === "WON") && accessAllowed(myPermissions, allPermissions.CREATE_SALES) && (
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Add {viewName}
                </Button>
              )}
              <Button
                shape="circle"
                type="primary"
                icon={<BiFilterAlt />}
                onClick={() => setFilterVisible(!filterVisible)}
              />
            </Space>
          </Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setFilterVisible(false)} />
          </div>
          <Form
            form={filterForm}
            layout="vertical"
            onFinish={(values) => {
              const min = values.probabilityMin;
              const max = values.probabilityMax;

              if (min !== undefined && min !== null && (min < 0 || min > 100)) {
                message.error("Minimum probability must be between 0 and 100");
                return;
              }

              if (max !== undefined && max !== null && (max < 0 || max > 100)) {
                message.error("Maximum probability must be between 0 and 100");
                return;
              }

              if (min != null && max != null && min > max) {
                message.error("Minimum probability cannot exceed maximum probability");
                return;
              }

              setFilters(values);
            }}
          >
            <Row gutter={12}>
              <Col xs={24} md={6}>
                <Form.Item name="entityId">
                  <Select mode="multiple" allowClear showSearch placeholder="Entity" options={entityList.map((e) => ({ value: e.id, label: e.name })).sort(compareOptionLabels)} />
                </Form.Item>
              </Col>
              {/* 2. Client Name */}
              <Col xs={24} md={6}>
                <Form.Item name="clientId">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder="Client Name"
                    options={clientfilterOptions}
                  />
                </Form.Item>
              </Col>

              {/* 3. Opportunity Type */}
              <Col xs={24} md={6}>
                <Form.Item name="opportunityType">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Opportunity Type"
                    options={projectTypes.map((t) => ({ value: t.id, label: t.name })).sort(compareOptionLabels)}
                  />
                </Form.Item>
              </Col>
              {/* Opportunity Filter */}
              <Col xs={24} md={6}>
                <Form.Item name="opportunity">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Opportunity"
                    options={filterOptions.opportunities}
                  />
                </Form.Item>
              </Col>
              {/* Minimum Probability */}
              <Col xs={24} md={3}>
                <Form.Item name="probabilityMin">
                  <InputNumber<number>
                    min={0}
                    max={100}
                    precision={2}
                    formatter={(value) =>
                      value === undefined || value === null
                        ? ""
                        : `${Number(value).toFixed(2)}%`
                    }
                    parser={(value) => {
                      const parsed = Number(value?.replace("%", ""));
                      return Number.isFinite(parsed) ? parsed : 0;
                    }}
                    style={{ width: "100%" }}
                    placeholder="Min Probability (%)"
                  />
                </Form.Item>
              </Col>

              {/* Maximum Probability */}
              <Col xs={24} md={3}>
                <Form.Item name="probabilityMax">
                  <InputNumber<number>
                    min={0}
                    max={100}
                    precision={2}
                    formatter={(value) =>
                      value === undefined || value === null
                        ? ""
                        : `${Number(value).toFixed(2)}%`
                    }
                    parser={(value) => {
                      const parsed = Number(value?.replace("%", ""));
                      return Number.isFinite(parsed) ? parsed : 0;
                    }}
                    style={{ width: "100%" }}
                    placeholder="Max Probability (%)"
                  />
                </Form.Item>
              </Col>

              {/* 4. Lead Owner */}
              <Col xs={24} md={6}>
                <Form.Item name="leadOwnerId">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Lead Owner"
                    options={filterOptions?.leadOwners ?? []}
                  />
                </Form.Item>
              </Col>

              {/* 5. Delivery Owner */}
              <Col xs={24} md={6}>
                <Form.Item name="deliveryOwnerId">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Delivery Owner"
                    options={filterOptions?.deliveryOwners ?? []}
                  />
                </Form.Item>
              </Col>

              {/* 6. Closure Quarter */}
              <Col xs={24} md={6}>
                <Form.Item name="closureQuarter">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Closure Quarter"
                    options={filterOptions?.closureQuarters ?? []}
                  />
                </Form.Item>
              </Col>
              {/* Temporarily disabled - Closure Month filter
              <Col xs={24} md={6}>
                <Form.Item name="closureMonth">
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Closure Month"
                    options={filterOptions?.closureMonths ?? []}
                  />
                </Form.Item>
              </Col>
               */}
              <Col xs={24} md={6}>
                <Form.Item name="dealStageId">
                  <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Deal Stage" options={stages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} />
                </Form.Item>
              </Col>

              {/* Temporarily disabled - Priority filter

              <Col xs={24} md={4}>
                <Form.Item name="priority">
                  <Select mode="multiple" allowClear showSearch placeholder="Priority" options={["1", "2", "3"].map((p) => ({ value: p, label: p }))} />
                </Form.Item>
              </Col>
              */}
              <Col xs={24} md={4}>
                <Space>
                  <Button
                    onClick={() => {
                      filterForm.resetFields();
                      setFilters({});
                      loadList(1);
                    }}
                  >
                    Clear
                  </Button>
                  <Button type="primary" htmlType="submit">
                    Apply
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          rowKey="salesId"
          pagination
          loading={loadingSales}
          dataSource={salesList}
          columns={columns}
          currentPage={pagination?.page || 1}
          totalRows={pagination?.total || 0}
          defaultPage={0}
          onChange={onChangeHandler}
          onPageChange={(page: number) => loadList(page)}
          onPageSizeChange={(_: number, size: number) => setPageSize(size)}
          pageSize={pageSize}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          tableWidth="max-content"
        />
      </div>

      <Modal
        centered
        width={1100}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        maskClosable={false}
        className="sales-edit-modal"
        title={<div className="it-asset-modal-title">
          <span className="it-asset-modal-title-icon"><FunnelPlotOutlined /></span>
          <span>{selectedEntity ? "Edit " + viewName : "Add " + viewName}</span>
        </div>}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        styles={{ body: { maxHeight: "70dvh", overflowY: "auto", overflowX: "hidden", paddingTop: 16 } }}
        footer={<Space>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button type="primary" disabled={generatingQuotation} onClick={() => form.submit()}>Save</Button>
        </Space>}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveSales} onValuesChange={lookupConversion}>
          <Row gutter={20} style={{ margin: 0 }}>
            <Col xs={24} sm={12} lg={8}><Form.Item name="entityId" label="Entity" rules={[{ required: true }]}><Select options={entityList.map((e) => ({ value: e.id, label: e.name })).sort(compareOptionLabels)} onChange={() => form.setFieldValue("clientId", undefined)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="receivedDate" label="Sale Received Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="clientId" label="Client" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" disabled={!selectedEntityId} options={clientOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="clientLocation" label="Client Location"><Select showSearch optionFilterProp="label" options={Country.getAllCountries().map((c) => ({ value: c.isoCode, label: c.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="clientCategory"
                label="Client Category"
              // rules={[
              //   {
              //     required: true,
              //     message: "Please select Client Category",
              //   },
              // ]}
              >
                <Select
                  placeholder="Select Client Category"
                  options={[
                    { value: "Legacy", label: "Legacy" },
                    { value: "Maturing", label: "Maturing" },
                    { value: "New", label: "New" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="projectTypeId" label="Opportunity Type" rules={[{ required: true }]}><Select options={projectTypes.map((t) => ({ value: t.id, label: t.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="opportunity" label="Opportunity Description" rules={[{ required: true, max: 100 }]}><Input maxLength={100} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="leadOwnerId" label="Lead Owner" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={staffOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="deliveryOwnerId" label="Delivery Owner" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={staffOptions} /></Form.Item></Col>
            {workflowStatus !== "WON" && workflowStatus !== "LOST" && (
              <Col xs={24} sm={12} lg={8}><Form.Item name="estimatedClosureDate" label="Est. Closure Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" disabledDate={(current) => !!(current && current < dayjs().startOf("day"))} /></Form.Item></Col>
            )}
            {workflowStatus === "LOST" && (<>
              {/* Lost Date */}
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="lostDate"
                  label="Lost Date"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
                </Form.Item>
              </Col>
            </>
            )}
            {workflowStatus === "WON" && (
              <>
                {/* Close Date */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="closeDate"
                    label="Closed Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
                  </Form.Item>
                </Col>

                {/* SOW / PO Number */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="sowPoNumber"
                    label="SOW / PO Number"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                {/* Estimated Start Date */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="estimatedStartDate"
                    label="Estimated Start Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MMM-YYYY"
                      onChange={calculateDuration}
                    />
                  </Form.Item>
                </Col>

                {/* Estimated End Date */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="estimatedEndDate"
                    label="Estimated End Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MMM-YYYY"
                      onChange={calculateDuration}
                    />
                  </Form.Item>
                </Col>

                {/* Duration */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="durationMonths"
                    label="Duration (Months)"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      precision={0}
                      disabled
                    />
                  </Form.Item>
                </Col>

                {/* Year Renewal */}
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    name="addInYear"
                    label="Year Revenue"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        {
                          value: "FY 2024-25",
                          label: "FY 2024-25",
                        },
                        {
                          value: "FY 2025-26",
                          label: "FY 2025-26",
                        },
                        {
                          value: "FY 2026-27",
                          label: "FY 2026-27",
                        },
                        {
                          value: "FY 2027-28",
                          label: "FY 2027-28",
                        },
                        {
                          value: "FY 2028-29",
                          label: "FY 2028-29",
                        },
                        {
                          value: "FY 2029-30",
                          label: "FY 2029-30",
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>

              </>
            )}

            {workflowStatus !== "WON" && workflowStatus !== "LOST" && (
              <>
                <Col xs={24} sm={12} lg={8}><Form.Item label="Closure Qtr"><Input disabled value={closureQuarter} /></Form.Item></Col>
                <Col xs={24} sm={12} lg={8}><Form.Item label="Closure Month"><Input disabled value={closureMonth} /></Form.Item></Col>
              </>
            )}
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealStageId" label="Deal Stage" rules={[{ required: true }]}><Select options={stages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} onChange={(value) => { form.setFieldValue("dealSubStageId", undefined); loadSubStages(value); }} /></Form.Item></Col>
            {workflowStatus !== "WON" && (
              <Col xs={24} sm={12} lg={8}><Form.Item name="dealSubStageId" label="Deal Sub-Stage"><Select allowClear disabled={!subStages.length} options={subStages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} /></Form.Item></Col>
            )}
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealCurrencyId" label="Deal Currency" rules={[{ required: true }]}><Select options={currencyOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="dealValue" label="Deal Value" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: "100%" }} formatter={(value) =>
              value !== undefined && value !== null
                ? Number(value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : ""
            }
            /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="targetCurrencyId" label="Reporting Currency" rules={[{ required: true }]}><Select options={currencyOptions} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item label={targetValueLabel}><Input disabled value={convertedValueText()
              ? Number(convertedValueText()).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              : ""
            }
            /></Form.Item></Col>
            <Col xs={24} sm={12} lg={8}><Form.Item name="grossMargin" label="Gross Margin (%)"><InputNumber addonAfter="%" min={0} max={100} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
            {workflowStatus !== "WON" && workflowStatus !== "LOST" && (
              <Col xs={24} sm={12} lg={8}><Form.Item name="probability" label="Probability (%)"><InputNumber addonAfter="%" min={0} max={100} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
            )}
            <Col xs={24} sm={12} lg={8}><Form.Item label="Quotation / SOW #">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Form.Item name="quotationSowNumber" noStyle><Input aria-label="Quotation / SOW number" disabled={generatingQuotation} style={{ flex: "1 1 160px", minWidth: 0 }} /></Form.Item>
                <Tooltip title="Generate Quotation Number"><Button icon={<FileAddOutlined />} aria-label="Generate Quotation Number"
                  loading={generatingQuotation} onClick={() => {
                    if (form.getFieldValue("quotationSowNumber")) {
                      Modal.confirm({ title: "Replace quotation number?", content: "Generate a new number to replace the current value?", okText: "Generate", onOk: generateQuotationNumber });
                    } else { generateQuotationNumber(); }
                  }}>Generate</Button></Tooltip>
              </div>
            </Form.Item></Col>
            {workflowStatus !== "WON" && workflowStatus !== "LOST" && (
              <Col xs={24} sm={12} lg={8}><Form.Item name="priority" label="Priority" rules={[{ required: true }]}><Select options={["1", "2", "3"].map((p) => ({ value: p, label: p }))} /></Form.Item></Col>
            )}
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="newRenewal"
                label="New/Renewal"
              // rules={[
              //   {
              //     required: true,
              //     message: "Please select New/Renewal",
              //   },
              // ]}
              >
                <Select
                  placeholder="Select New/Renewal"
                  options={[
                    { value: "New", label: "New" },
                    { value: "Renewal", label: "Renewal" },
                  ]}
                />
              </Form.Item>
            </Col>
            {/* PO / Contract Attachment — Add/Edit Sales */}
            <Col xs={24}>
              <Form.Item label="PO / Contract Attachment">
                <Upload.Dragger
                  accept=".pdf,.doc,.docx"
                  maxCount={10}
                  multiple
                  beforeUpload={(file) => {
                    const extension = file.name
                      .substring(file.name.lastIndexOf("."))
                      .toLowerCase();

                    if (![".pdf", ".doc", ".docx"].includes(extension)) {
                      showNotification("Only PDF, DOC, and DOCX files are allowed", "error");
                      return Upload.LIST_IGNORE;
                    }

                    if (file.size > 10 * 1024 * 1024) {
                      showNotification("File size must not exceed 10 MB", "error");
                      return Upload.LIST_IGNORE;
                    }

                    if (existingSalesAttachments.length + salesAttachments.length >= 10) {
                      showNotification("Maximum 10 attachments allowed", "error");
                      return Upload.LIST_IGNORE;
                    }

                    setSalesAttachments((previous) => [...previous, file]);
                    return false;
                  }}
                  onRemove={(file) => {
                    const isExistingAttachment = existingSalesAttachments.some(
                      (attachment) => String(attachment.id) === String(file.uid)
                    );

                    // Don't delete server-side attachments through UI-only removal.
                    if (isExistingAttachment) {
                      return false;
                    }

                    setSalesAttachments((previous) =>
                      previous.filter(
                        (item) => `${item.name}-${item.size}` !== file.uid
                      )
                    );

                    return true;
                  }}
                  onPreview={handleSalesAttachmentPreview}
                  fileList={[
                    ...existingSalesAttachments.map((attachment) => ({
                      uid: String(attachment.id),
                      name:
                        attachment.originalName ||
                        attachment.fileName ||
                        attachment.name ||
                        "Existing PO / Contract attachment",
                      status: "done" as const,
                    })),
                    ...salesAttachments.map((file) => ({
                      uid: `${file.name}-${file.size}`,
                      name: file.name,
                      status: "done" as const,
                      originFileObj: file,
                    })),
                  ]}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag the PO/Contract file here
                  </p>
                  <p className="ant-upload-hint">
                    Accepted formats: PDF, DOC, DOCX. Maximum size: 10 MB.
                    Click an existing file to download it.
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>


            <Col span={24}><Form.Item name="remarks" label="Remarks"><Input.TextArea maxLength={500} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title="Convert to WON"
        open={wonModalOpen}
        onCancel={() => setWonModalOpen(false)}
        okText="Convert to WON"
        cancelText="Cancel"
        onOk={() => wonForm.submit()}
        confirmLoading={submittingWon}
        okButtonProps={{
          disabled: submittingWon || !!convertingTo,
          style: {
            background: "#15803d", borderColor: "#bbf7d0"
          },
        }}
      >
        <Form
          form={wonForm}
          layout="vertical"
          onFinish={handleWonSubmit}
        >
          <Row gutter={16}>

            {/* SOW / PO Number */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="sowPoNumber"
                label="SOW / PO Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter SOW / PO Number",
                  },
                ]}
              >
                <Input placeholder="Enter SOW / PO Number" />
              </Form.Item>
            </Col>

            {/* Estimated Start Date */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="estimatedStartDate"
                label="Estimated Start Date"
                rules={[
                  {
                    required: true,
                    message: "Please select Estimated Start Date",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
            </Col>

            {/* Estimated End Date */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="estimatedEndDate"
                label="Estimated End Date"
                dependencies={["estimatedStartDate"]}
                rules={[
                  {
                    required: true,
                    message: "Please select Estimated End Date",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("estimatedStartDate");

                      if (!value || !startDate) {
                        return Promise.resolve();
                      }

                      if (dayjs(value).isBefore(dayjs(startDate), "day")) {
                        return Promise.reject(
                          new Error(
                            "Estimated End Date cannot be before Start Date",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
            </Col>

            {/* Duration */}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Duration (Months)"
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.estimatedStartDate !==
                  currentValues.estimatedStartDate ||
                  prevValues.estimatedEndDate !==
                  currentValues.estimatedEndDate
                }
              >
                {({ getFieldValue }) => {
                  const startDate = getFieldValue("estimatedStartDate");
                  const endDate = getFieldValue("estimatedEndDate");

                  let duration = "";

                  if (startDate && endDate) {
                    const start = dayjs(startDate);
                    const end = dayjs(endDate);

                    if (!end.isBefore(start, "day")) {
                      duration = String(
                        Math.ceil(end.diff(start, "month", true)),
                      );
                    }
                  }

                  return (
                    <Input
                      value={duration}
                      disabled
                    />
                  );
                }}
              </Form.Item>
            </Col>
            {/* Close Date */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="closeDate"
                label="Closed Date"
                rules={[
                  {
                    required: true,
                    message: "Please select Closed Date",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MMM-YYYY"
                />
              </Form.Item>
            </Col>
            {/* Add in Year */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="addInYear"
                label="Year Revenue"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    {
                      value: "FY 2024-25",
                      label: "FY 2024-25",
                    },
                    {
                      value: "FY 2025-26",
                      label: "FY 2025-26",
                    },
                    {
                      value: "FY 2026-27",
                      label: "FY 2026-27",
                    },
                    {
                      value: "FY 2027-28",
                      label: "FY 2027-28",
                    },
                    /* {
                       value: "FY 2028-29",
                       label: "FY 2028-29",
                     },
                     {
                       value: "FY 2029-30",
                       label: "FY 2029-30",
                     }*/
                  ]}
                />
              </Form.Item>
            </Col>

            {/* PO / Contract Attachment — Convert to WON only */}
            <Col xs={24}>
              <Form.Item label="PO / Contract Attachment (Optional)">
                <Upload.Dragger
                  accept=".pdf,.doc,.docx"
                  maxCount={1}
                  multiple={false}
                  beforeUpload={(file) => {
                    const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

                    if (![".pdf", ".doc", ".docx"].includes(extension)) {
                      showNotification("Only PDF, DOC, and DOCX files are allowed", "error");
                      return Upload.LIST_IGNORE;
                    }

                    if (file.size > 10 * 1024 * 1024) {
                      showNotification("File size must not exceed 10 MB", "error");
                      return Upload.LIST_IGNORE;
                    }

                    setWonAttachment(file);
                    return false;
                  }}
                  onRemove={() => {
                    setWonAttachment(null);
                    return true;
                  }}
                  fileList={
                    wonAttachment
                      ? [
                        {
                          uid: `${wonAttachment.name}-${wonAttachment.size}`,
                          name: wonAttachment.name,
                          status: "done" as const,
                        },
                      ]
                      : []
                  }
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Accepted formats: PDF, DOC, DOCX. Maximum size: 10 MB.
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title="Convert to LOST"
        open={lostModalOpen}
        onCancel={() => setLostModalOpen(false)}
        okText="Convert to LOST"
        cancelText="Cancel"
        onOk={() => lostForm.submit()}
        width={700}
      >
        <Form
          form={lostForm}
          layout="vertical"
          onFinish={handleLostSubmit}
        >
          <Row gutter={16}>
            {/* Deal Stage */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="dealStageId"
                label="Deal Stage"
                rules={[
                  {
                    required: true,
                    message: "Please select Deal Stage",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Deal Stage"
                  optionFilterProp="label"
                  options={stages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)} onChange={(value) => { form.setFieldValue("dealSubStageId", undefined); loadSubStages(value); }}
                />
              </Form.Item>
            </Col>

            {/* Deal Sub Stage */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="dealSubStageId"
                label="Deal Sub Stage"
                rules={[
                  {
                    required: true,
                    message: "Please select Deal Sub Stage",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Deal Sub Stage"
                  optionFilterProp="label"
                  options={subStages.map((s) => ({ value: s.id, label: s.name })).sort(compareOptionLabels)}
                />
              </Form.Item>
            </Col>
            {/* Lost Date */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="lostDate"
                label="Lost Date"
                rules={[
                  {
                    required: true,
                    message: "Please select Lost Date",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                />
              </Form.Item>
            </Col>

          </Row>
        </Form>
      </Modal>
    </div>


  );
};

export default ManageSalesPage;
